import { useId, type InputHTMLAttributes, type ReactElement, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useFieldRow } from '../FieldRow/FieldRow';
import styles from './Field.module.scss';

interface FieldShellProps {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Поле рендерится функцией, получающей id и признаки доступности. */
  children: (control: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/** Обёртка поля: подпись, подсказка, ошибка, связи aria. */
export function FormField({ label, hint, error, required, children }: FieldShellProps): ReactElement {
  const id = useId();
  const row = useFieldRow();
  const labelInRow = row === 'sm' ? styles.labelInRowSm : row === 'md' ? styles.labelInRowMd : '';
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={`${styles.label} ${required ? styles.required : ''} ${labelInRow}`} title={row ? label : undefined}>
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

type Common = { label: string; hint?: ReactNode; error?: string | null };

export type TextFieldProps = Common & InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> };

export function TextField({ label, hint, error, required, ...input }: TextFieldProps): ReactElement {
  return (
    <FormField label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <input id={id} className={styles.control} aria-describedby={describedBy} aria-invalid={invalid} required={required} {...input} />
      )}
    </FormField>
  );
}

export type TextAreaProps = Common & TextareaHTMLAttributes<HTMLTextAreaElement> & { maxCounter?: number | null; ref?: Ref<HTMLTextAreaElement> };

export function TextArea({ label, hint, error, required, maxCounter, value, ...rest }: TextAreaProps): ReactElement {
  const length = typeof value === 'string' ? value.length : 0;
  return (
    <FormField label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <>
          <textarea
            id={id}
            className={`${styles.control} ${styles.textarea}`}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            required={required}
            value={value}
            {...rest}
          />
          {maxCounter !== undefined ? (
            <span className={`${styles.counter} ${maxCounter !== null && length > maxCounter ? styles.counterOver : ''}`} aria-live="polite">
              {maxCounter === null ? length : `${length} / ${maxCounter}`}
            </span>
          ) : null}
        </>
      )}
    </FormField>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  /** Подпись группы: подряд идущие варианты одной группы выводятся в `<optgroup>`. */
  group?: string;
}

function SelectOptions({ options }: { options: readonly SelectOption[] }): ReactElement {
  const runs: { group: string | undefined; options: SelectOption[] }[] = [];
  for (const option of options) {
    const last = runs.at(-1);
    if (last && last.group === option.group) last.options.push(option);
    else runs.push({ group: option.group, options: [option] });
  }
  const render = (list: readonly SelectOption[]): ReactElement[] =>
    list.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ));
  return (
    <>
      {runs.map((run, index) =>
        run.group === undefined ? (
          render(run.options)
        ) : (
          <optgroup key={`${run.group}-${index}`} label={run.group}>
            {render(run.options)}
          </optgroup>
        ),
      )}
    </>
  );
}

export type SelectFieldProps = Common & SelectHTMLAttributes<HTMLSelectElement> & { options: readonly SelectOption[]; placeholder?: string; ref?: Ref<HTMLSelectElement> };

export function SelectField({ label, hint, error, required, options, placeholder, ...rest }: SelectFieldProps): ReactElement {
  return (
    <FormField label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <select id={id} className={`${styles.control} ${styles.select}`} aria-describedby={describedBy} aria-invalid={invalid} required={required} {...rest}>
          {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
          <SelectOptions options={options} />
        </select>
      )}
    </FormField>
  );
}

/** Сырой список выбора для использования внутри FormField (id и aria приходят оттуда). */
export function Select({ options, placeholder, ...rest }: { options: readonly SelectOption[]; placeholder?: string } & SelectHTMLAttributes<HTMLSelectElement>): ReactElement {
  return (
    <select className={`${styles.control} ${styles.select}`} {...rest}>
      {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
      <SelectOptions options={options} />
    </select>
  );
}

/** Поле без подписи (фильтры, таблицы) — подпись уходит в aria-label. */
export function BareSelect({
  label,
  options,
  ...rest
}: { label: string; options: readonly SelectOption[] } & SelectHTMLAttributes<HTMLSelectElement>): ReactElement {
  return (
    <select aria-label={label} className={`${styles.control} ${styles.select}`} {...rest}>
      <SelectOptions options={options} />
    </select>
  );
}

export function BareInput({ label, ...rest }: { label: string } & InputHTMLAttributes<HTMLInputElement>): ReactElement {
  return <input aria-label={label} className={styles.control} {...rest} />;
}

export type CheckboxProps = { label: ReactNode; ref?: Ref<HTMLInputElement> } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export function Checkbox({ label, ...rest }: CheckboxProps): ReactElement {
  return (
    <label className={styles.checkbox}>
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}
